import { useEffect, useRef, useState } from "react";
import { FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import RichTextEditor from "./RichTextEditor";

const TABS = [["generali", "Generali"], ["impostazioni", "Impostazioni"],
              ["landing", "Landing page"], ["thankyou", "Thank you page"],
              ["pdf", "PDF"], ["email", "Email"], ["condividi", "Condividi"]];

const FIELD_DEFS = [
  ["nome", "Nome", true], ["cognome", "Cognome", false], ["telefono", "Telefono", true],
  ["email", "Email", false], ["data_nascita", "Data di nascita", false],
  ["indirizzo", "Indirizzo", false], ["citta", "Città", false], ["cap", "CAP", false],
  ["provincia", "Provincia", false], ["nazione", "Nazione", false],
];

const CTA_PRESETS = ["Scarica ora il tuo coupon", "Ricevi subito l'offerta", "Ricevi gratis il tuo omaggio",
                     "Richiedi lo sconto", "Voglio maggiori informazioni"];
const RINNOVA_OPTS = [[6, "6 ore"], [12, "12 ore"], [24, "24 ore"], [48, "2 giorni"], [72, "3 giorni"],
                      [168, "7 giorni"], [336, "14 giorni"], [504, "21 giorni"], [672, "28 giorni"]];

const DEFAULTS = {
  name: "", description: "", valid_from: "", valid_to: "", conditions: "", campaign_id: "",
  active: true, tipo: "promozionale", codice: "", web_form_id: "",
  fields: { nome: { on: true, req: true }, cognome: { on: true, req: true },
            telefono: { on: true, req: true }, email: { on: true, req: false } },
  landing: { titolo: "", sottotitolo: "", descrizione: "", condizioni: "", immagine: "", img_fit: "adatta",
             cta_label: "", video_youtube: "", countdown: "no", countdown_scadenza: "blocca",
             rinnova_ore: 24, mostra_disponibilita: false,
             colore_titolo: "", colore_sottotitolo: "", colore_testo: "",
             dim_titolo: "grande", sfondo_header: "" },
  thankyou: { titolo: "", descrizione: "", modalita: "pagina", redirect_url: "" },
  tracciamenti: { ga4_event: "", fb_event: "" },
  pdf: { attivo: false, titolo: "", testo: "", show_qr: true },
  email: { subject: "", titolo: "", testo: "", cta_label: "" },
  referral: { attivo: false, messaggio: "" },
  offers: [], web_form_id_vetrina: "", vetrina_booking_first: true,
  scadenza_mode: "date", giorni_validita: 15, quantita: "", numero_utilizzi: 1,
  disponibile_fino_al: "", riservato_a: "tutti",
  tag_download: "", tag_utilizzo: "", tag_non_utilizzato: "", tag_prenotato: "",
};

const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";

// Selettore colore con ripristino al valore predefinito (stringa vuota = default del tema)
function ColorField({ label, value, onChange, fallback, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 rounded-md border border-zinc-300 bg-white p-1 cursor-pointer" data-testid={testid} />
        <span className="text-xs text-zinc-500 font-mono flex-1">{value || "predefinito"}</span>
        {value && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-reset`}
            className="h-8 w-8 rounded-md border border-zinc-300 text-zinc-500 flex items-center justify-center hover:text-zinc-900">
            <X size={13} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

// Select tag con creazione inline (catalogo Tag centralizzati di Impostazioni)
function TagSelect({ label, value, onChange, tags, onCreated, testid }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const create = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await api.post("/tags", { name: n });
      onCreated();
      onChange(n);
      setAdding(false);
      setName("");
      toast.success(`Tag "${n}" creato e selezionato`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
        <option value="">Nessun tag</option>
        {value && !tags.some((t) => t.name === value) && (
          <option value={value}>{value} (non in catalogo)</option>
        )}
        {tags.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
      </select>
      {!adding ? (
        <button type="button" onClick={() => setAdding(true)} data-testid={`${testid}-new`}
          className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 mt-1">+ Crea nuovo tag</button>
      ) : (
        <div className="flex gap-2 mt-1">
          <input value={name} onChange={(e) => setName(e.target.value.toUpperCase())} placeholder="NOME-TAG"
            className={inputCls} data-testid={`${testid}-new-input`} />
          <button type="button" onClick={create} data-testid={`${testid}-new-save`}
            className="h-10 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold shrink-0">Crea</button>
        </div>
      )}
    </div>
  );
}

// Editor coupon a tab (creazione manuale completa / modifica). initial puo' essere
// una bozza AI o un coupon esistente; salvataggio interno POST/PATCH /coupons.
export default function CouponEditor({ initial, campaigns, webForms, onClose, onSaved }) {
  const editingId = initial?.id || null;
  const [tab, setTab] = useState("generali");
  const [form, setForm] = useState(() => ({
    ...DEFAULTS, ...(initial || {}),
    fields: { ...DEFAULTS.fields, ...((initial || {}).fields || {}) },
    landing: { ...DEFAULTS.landing, ...((initial || {}).landing || {}) },
    thankyou: { ...DEFAULTS.thankyou, ...((initial || {}).thankyou || {}) },
    pdf: { ...DEFAULTS.pdf, ...((initial || {}).pdf || {}) },
    email: { ...DEFAULTS.email, ...((initial || {}).email || {}) },
    referral: { ...DEFAULTS.referral, ...((initial || {}).referral || {}) },
    tracciamenti: { ...DEFAULTS.tracciamenti, ...((initial || {}).tracciamenti || {}) },
    campaign_id: (initial || {}).campaign_id || "",
    web_form_id: (initial || {}).web_form_id || "",
  }));
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const [coupons, setCoupons] = useState([]);

  useEffect(() => {
    if (form.tipo === "vetrina") api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  }, [form.tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadTags = () => api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
  useEffect(() => { reloadTags(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOffer = (i, k, v) => set("offers", (form.offers || []).map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  const setNested = (k, sub, v) => setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), [sub]: v } }));
  const setField = (key, prop, v) => setForm((f) => ({
    ...f, fields: { ...f.fields, [key]: { on: false, req: false, ...(f.fields || {})[key], [prop]: v } },
  }));

  const fileRef = useRef(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const uploadLandingImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)"); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/marketing/uploads/image", fd);
      setNested("landing", "immagine", r.data.url);
      toast.success("Immagine caricata");
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingImg(false); }
  };

  const previewPdf = async () => {
    if (!editingId) { toast.info("Salva il coupon per generare l'anteprima PDF"); return; }
    try {
      const r = await api.get(`/coupons/${editingId}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Il nome del coupon è obbligatorio");
      setTab("generali");
      return;
    }
    if (form.scadenza_mode === "date" && (!form.valid_from || !form.valid_to)) {
      toast.error("Imposta le date di validità oppure scegli la modalità a giorni");
      setTab("impostazioni");
      return;
    }
    if (form.scadenza_mode === "giorni" && !(parseInt(form.giorni_validita) >= 1)) {
      toast.error("Giorni di validità non validi (minimo 1)");
      setTab("impostazioni");
      return;
    }
    if (form.tipo === "vetrina" && !(form.offers || []).some((o) => o.coupon_id)) {
      toast.error("La vetrina richiede almeno un'offerta collegata a un coupon");
      setTab("generali");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, campaign_id: form.campaign_id || null, web_form_id: form.web_form_id || null,
        quantita: form.quantita ? parseInt(form.quantita) : null,
        giorni_validita: parseInt(form.giorni_validita) || 15,
        numero_utilizzi: Number.isNaN(parseInt(form.numero_utilizzi)) ? 1 : parseInt(form.numero_utilizzi),
        disponibile_fino_al: form.disponibile_fino_al || null };
      if (editingId) await api.patch(`/coupons/${editingId}`, payload);
      else await api.post("/coupons", payload);
      toast.success(editingId ? "Coupon salvato" : "Coupon creato");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto p-4" onClick={onClose}>
      <div className="max-w-2xl mx-auto my-8 bg-zinc-50 rounded-xl" onClick={(e) => e.stopPropagation()}
        data-testid="coupon-editor">
        <div className="flex items-center justify-between px-5 pt-4">
          <h3 className="font-display text-lg font-bold text-zinc-900">
            {editingId ? `Modifica coupon — ${form.name}` : "Nuovo coupon (manuale completa)"}
          </h3>
          <button onClick={onClose} data-testid="ced-close"
            className="h-8 w-8 rounded-md flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex gap-1 px-5 mt-2 border-b border-zinc-200" data-testid="ced-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ced-tab-${k}`}
              className={`px-3 py-2 text-xs font-semibold border-b-2 transition ${tab === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {tab === "generali" && (
            <>
              <div>
                <label className={labelCls}>Tipologia *</label>
                <div className="flex gap-2">
                  {[["promozionale", "Promozionale"], ["referral", "Referral"], ["vetrina", "Vetrina offerte"]].map(([k, l]) => (
                    <button type="button" key={k} onClick={() => set("tipo", k)} data-testid={`ced-tipo-${k}`}
                      className={`h-10 px-5 rounded-md border text-sm font-semibold transition ${form.tipo === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {form.tipo === "vetrina" && (
                <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-3" data-testid="ced-offerte">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Offerte della vetrina</div>
                  <p className="text-[11px] text-zinc-400">
                    Il cliente vede queste offerte sulla pagina pubblica, ne sceglie una e scarica il coupon collegato.
                    Crea prima i singoli coupon, poi collegali qui.
                  </p>
                  {(form.offers || []).map((o, i) => (
                    <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2" data-testid={`ced-offerta-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-zinc-700">Offerta {i + 1}</span>
                        <button type="button" onClick={() => set("offers", form.offers.filter((_, j) => j !== i))}
                          data-testid={`ced-offerta-${i}-remove`}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                      <input value={o.titolo || ""} onChange={(e) => setOffer(i, "titolo", e.target.value)}
                        placeholder="Titolo (es. Compleanno? Mangi gratis)" className={inputCls} data-testid={`ced-offerta-${i}-titolo`} />
                      <input value={o.sottotitolo || ""} onChange={(e) => setOffer(i, "sottotitolo", e.target.value)}
                        placeholder="Sottotitolo breve" className={inputCls} data-testid={`ced-offerta-${i}-sottotitolo`} />
                      <textarea value={o.descrizione || ""} onChange={(e) => setOffer(i, "descrizione", e.target.value)}
                        placeholder="Descrizione / condizioni visibili al cliente" rows={2} data-testid={`ced-offerta-${i}-descrizione`}
                        className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                      <select value={o.coupon_id || ""} onChange={(e) => setOffer(i, "coupon_id", e.target.value)}
                        className={inputCls} data-testid={`ced-offerta-${i}-coupon`}>
                        <option value="">— Collega un coupon —</option>
                        {coupons.filter((c) => c.id !== editingId && c.tipo !== "vetrina").map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {(form.offers || []).length < 4 && (
                    <button type="button" data-testid="ced-offerta-add"
                      onClick={() => set("offers", [...(form.offers || []), { titolo: "", sottotitolo: "", descrizione: "", coupon_id: "" }])}
                      className="w-full h-10 rounded-md border-2 border-dashed border-zinc-300 text-xs font-semibold text-zinc-500 hover:border-zinc-500 hover:text-zinc-700 transition">
                      + Aggiungi offerta (max 4)
                    </button>
                  )}
                  <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer" data-testid="ced-booking-first">
                    <input type="checkbox" checked={form.vetrina_booking_first}
                      onChange={(e) => set("vetrina_booking_first", e.target.checked)}
                      className="mt-0.5 rounded border-zinc-300" />
                    <span>
                      <b>Coupon solo dopo la prenotazione</b> — il cliente non può scaricare il coupon direttamente:
                      sceglie l'offerta, prenota dal modulo collegato e riceve il coupon in base ai coperti.
                    </span>
                  </label>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Nome *</label>
                  <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="ced-nome" /></div>
                <div><label className={labelCls}>Codice pubblico</label>
                  <input value={form.codice} onChange={(e) => set("codice", e.target.value.toUpperCase())} className={inputCls}
                    placeholder="es. DOLCEGRATIS" data-testid="ced-codice" /></div>
              </div>
              <div><label className={labelCls}>Descrizione breve</label>
                <input value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls}
                  placeholder="es. Dolce omaggio a cena" data-testid="ced-desc" /></div>
              <div>
                <label className={labelCls}>Campi anagrafica *</label>
                <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="ced-campi">
                  {FIELD_DEFS.map(([key, label, locked]) => {
                    const c = (form.fields || {})[key] || { on: false, req: false };
                    const on = locked || c.on;
                    const req = locked || c.req;
                    return (
                      <div key={key} className="flex items-center gap-4 px-3 py-2" data-testid={`ced-campo-${key}`}>
                        <label className="flex items-center gap-2 text-sm text-zinc-800 flex-1">
                          <input type="checkbox" checked={on} disabled={locked}
                            onChange={(e) => setField(key, "on", e.target.checked)} data-testid={`ced-campo-${key}-on`}
                            className="h-4 w-4 accent-zinc-900" />
                          {label} {locked && <span className="text-[10px] text-zinc-400">(sempre attivo)</span>}
                        </label>
                        <label className={`flex items-center gap-2 text-xs ${on && !locked ? "text-zinc-700" : "text-zinc-300"}`}>
                          <input type="checkbox" checked={req} disabled={locked || !on}
                            onChange={(e) => setField(key, "req", e.target.checked)} data-testid={`ced-campo-${key}-req`}
                            className="h-4 w-4 accent-zinc-900" />
                          Obbligatorio
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {tab === "impostazioni" && (
            <>
              <div>
                <label className={labelCls}>Modalità di scadenza *</label>
                <select value={form.scadenza_mode} onChange={(e) => set("scadenza_mode", e.target.value)} className={inputCls} data-testid="ced-scadenza-mode">
                  <option value="date">Date di validità fisse (dal/al)</option>
                  <option value="giorni">Utilizzabile entro X giorni dallo scaricamento</option>
                </select>
              </div>
              {form.scadenza_mode === "date" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Valido dal *</label>
                    <input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} className={inputCls} data-testid="ced-from" /></div>
                  <div><label className={labelCls}>Valido al *</label>
                    <input type="date" value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)} className={inputCls} data-testid="ced-to" /></div>
                </div>
              ) : (
                <div><label className={labelCls}>Giorni validità *</label>
                  <input type="number" min={1} value={form.giorni_validita} onChange={(e) => set("giorni_validita", e.target.value)} className={inputCls} data-testid="ced-giorni" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Quantità disponibile</label>
                  <input type="number" min={1} value={form.quantita} onChange={(e) => set("quantita", e.target.value)} className={inputCls}
                    placeholder="Vuoto = illimitata" data-testid="ced-quantita" /></div>
                <div><label className={labelCls}>Numero utilizzi *</label>
                  <select value={form.numero_utilizzi} onChange={(e) => set("numero_utilizzi", e.target.value)} className={inputCls} data-testid="ced-num-utilizzi">
                    <option value={1}>1 utilizzo</option>
                    <option value={0}>Nessun limite di utilizzo</option>
                    <option value={2}>2 utilizzi</option>
                    <option value={3}>3 utilizzi</option>
                    <option value={5}>5 utilizzi</option>
                    <option value={10}>10 utilizzi</option>
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Disponibile fino al</label>
                  <input type="date" value={form.disponibile_fino_al} onChange={(e) => set("disponibile_fino_al", e.target.value)} className={inputCls} data-testid="ced-disp-fino" />
                  <p className="text-[11px] text-zinc-400 mt-1">Ultimo giorno in cui è scaricabile. Vuoto = sempre disponibile.</p></div>
                <div><label className={labelCls}>Riservato a *</label>
                  <select value={form.riservato_a} onChange={(e) => set("riservato_a", e.target.value)} className={inputCls} data-testid="ced-riservato">
                    <option value="tutti">Coupon scaricabile da tutti</option>
                    <option value="solo_clienti">Solo clienti già in rubrica</option>
                  </select></div>
              </div>
              <div><label className={labelCls}>Condizioni (testo breve, visibile allo scanner)</label>
                <input value={form.conditions} onChange={(e) => set("conditions", e.target.value)} className={inputCls} data-testid="ced-conditions" /></div>
              <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-3" data-testid="ced-tags">
                <TagSelect label="Tag da applicare al download" value={form.tag_download} onChange={(v) => set("tag_download", v)} tags={tags} onCreated={reloadTags} testid="ced-tag-download" />
                <TagSelect label="Tag da applicare all'utilizzo" value={form.tag_utilizzo} onChange={(v) => set("tag_utilizzo", v)} tags={tags} onCreated={reloadTags} testid="ced-tag-utilizzo" />
                <TagSelect label="Tag da applicare alla prenotazione" value={form.tag_prenotato} onChange={(v) => set("tag_prenotato", v)} tags={tags} onCreated={reloadTags} testid="ced-tag-prenotato" />
                <TagSelect label="Tag da applicare se non utilizzato" value={form.tag_non_utilizzato} onChange={(v) => set("tag_non_utilizzato", v)} tags={tags} onCreated={reloadTags} testid="ced-tag-non-utilizzato" />
              </div>
              <div>
                <label className={labelCls}>Modulo Web collegato</label>
                <select value={form.tipo === "vetrina" ? (form.web_form_id_vetrina || "") : form.web_form_id}
                  onChange={(e) => set(form.tipo === "vetrina" ? "web_form_id_vetrina" : "web_form_id", e.target.value)}
                  className={inputCls} data-testid="ced-webform">
                  <option value="">Nessuno — conferma con link prenotazione standard</option>
                  {webForms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <p className="text-[11px] text-zinc-400 mt-1">
                  {form.tipo === "vetrina"
                    ? "Dopo aver scaricato il coupon, il cliente vede il bottone 'Prenota ora il tuo tavolo' che apre questo modulo."
                    : "Il link del modulo arriva nella conferma (WhatsApp/email) dopo il download: chi prenota dal modulo si aggancia in automatico a coupon + etichetta."}
                </p>
              </div>
              <div>
                <label className={labelCls}>Campagna collegata (opzionale)</label>
                <select value={form.campaign_id} onChange={(e) => set("campaign_id", e.target.value)} className={inputCls} data-testid="ced-campaign">
                  <option value="">Nessuna campagna</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-800">
                <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)}
                  className="h-4 w-4 accent-zinc-900" data-testid="ced-attivo" />
                Coupon attivo (scaricabile dal link pubblico)
              </label>
            </>
          )}

          {tab === "landing" && (
            <>
              <div>
                <label className={labelCls}>Immagine in testa alla pagina</label>
                {form.landing.immagine ? (
                  <div className="relative">
                    <img src={form.landing.immagine} alt="Anteprima immagine landing"
                      className="w-full h-36 object-cover rounded-md border border-zinc-200" data-testid="ced-landing-immagine-preview" />
                    <button type="button" onClick={() => setNested("landing", "immagine", "")} data-testid="ced-landing-immagine-remove"
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition">
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingImg} data-testid="ced-landing-immagine-upload"
                    className="w-full h-20 rounded-md border-2 border-dashed border-zinc-300 text-xs font-semibold text-zinc-500 hover:border-zinc-500 hover:text-zinc-700 transition disabled:opacity-50">
                    {uploadingImg ? "Caricamento in corso..." : "+ Carica immagine (JPEG/PNG, max 5 MB)"}
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={uploadLandingImage} data-testid="ced-landing-immagine-input" />
                <p className="text-[11px] text-zinc-400 mt-1">Viene mostrata in cima alla pagina pubblica del coupon, sopra il titolo.</p>
              </div>
              <div>
                <label className={labelCls}>Adattamento immagine</label>
                <select value={form.landing.img_fit || "adatta"} onChange={(e) => setNested("landing", "img_fit", e.target.value)}
                  className={inputCls} data-testid="ced-landing-imgfit">
                  <option value="adatta">Adatta — lo spazio segue il JPEG, nessun taglio</option>
                  <option value="copri">Copri — banner fisso con ritaglio (come prima)</option>
                </select>
              </div>
              <div><label className={labelCls}>Titolo</label>
                <input value={form.landing.titolo} onChange={(e) => setNested("landing", "titolo", e.target.value)} className={inputCls} data-testid="ced-landing-titolo" /></div>
              <div><label className={labelCls}>Sottotitolo</label>
                <input value={form.landing.sottotitolo} onChange={(e) => setNested("landing", "sottotitolo", e.target.value)} className={inputCls} data-testid="ced-landing-sottotitolo" /></div>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-3" data-testid="ced-landing-stile">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Stile della pagina pubblica</div>
                <div><label className={labelCls}>Dimensione titolo</label>
                  <select value={form.landing.dim_titolo || "grande"} onChange={(e) => setNested("landing", "dim_titolo", e.target.value)}
                    className={inputCls} data-testid="ced-landing-dimtitolo">
                    <option value="grande">Grande</option>
                    <option value="media">Media</option>
                    <option value="piccola">Piccola</option>
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <ColorField label="Colore titolo" value={form.landing.colore_titolo} fallback="#18181b"
                    onChange={(v) => setNested("landing", "colore_titolo", v)} testid="ced-landing-coloretitolo" />
                  <ColorField label="Colore sottotitolo" value={form.landing.colore_sottotitolo} fallback="#71717a"
                    onChange={(v) => setNested("landing", "colore_sottotitolo", v)} testid="ced-landing-coloresottotitolo" />
                  <ColorField label="Colore testo" value={form.landing.colore_testo} fallback="#3f3f46"
                    onChange={(v) => setNested("landing", "colore_testo", v)} testid="ced-landing-coloretesto" />
                  <ColorField label="Sfondo testata" value={form.landing.sfondo_header} fallback="#eef6fa"
                    onChange={(v) => setNested("landing", "sfondo_header", v)} testid="ced-landing-sfondoheader" />
                </div>
              </div>
              <div><label className={labelCls}>Descrizione</label>
                <RichTextEditor value={form.landing.descrizione} onChange={(v) => setNested("landing", "descrizione", v)} testid="ced-landing-descrizione" /></div>
              <div><label className={labelCls}>Condizioni di utilizzo</label>
                <RichTextEditor value={form.landing.condizioni} onChange={(v) => setNested("landing", "condizioni", v)} testid="ced-landing-condizioni" />
                <p className="text-[11px] text-zinc-400 mt-1">Non appaiono più in fondo alla pagina: si aprono in una finestra a tutto schermo dal pulsante "Condizioni di utilizzo".</p></div>
              <div><label className={labelCls}>Allineamento testo (descrizione e condizioni)</label>
                <select value={form.landing.allinea_testo || "sinistra"} onChange={(e) => setNested("landing", "allinea_testo", e.target.value)}
                  className={inputCls} data-testid="ced-landing-allinea">
                  <option value="sinistra">Allineato a sinistra</option>
                  <option value="centro">Centrato</option>
                </select></div>
              <div><label className={labelCls}>Testo del bottone (CTA)</label>
                <select value={form.landing.cta_label} onChange={(e) => setNested("landing", "cta_label", e.target.value)}
                  className={inputCls} data-testid="ced-landing-cta">
                  <option value="">Scarica il coupon (predefinito)</option>
                  {CTA_PRESETS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label className={labelCls}>Video YouTube (codice del video, facoltativo)</label>
                <input value={form.landing.video_youtube} onChange={(e) => setNested("landing", "video_youtube", e.target.value)}
                  placeholder="es. dQw4w9WgXcQ" className={inputCls} data-testid="ced-landing-video" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Conto alla rovescia</label>
                  <select value={form.landing.countdown} onChange={(e) => setNested("landing", "countdown", e.target.value)}
                    className={inputCls} data-testid="ced-landing-countdown">
                    <option value="no">Non mostrare conto alla rovescia</option>
                    <option value="mostra">Mostra conto alla rovescia</option>
                  </select></div>
                <div><label className={labelCls}>Alla scadenza del countdown</label>
                  <select value={form.landing.countdown_scadenza} onChange={(e) => setNested("landing", "countdown_scadenza", e.target.value)}
                    className={inputCls} data-testid="ced-landing-cdscadenza">
                    <option value="blocca">Blocca il modulo</option>
                    <option value="rinnova">Rinnova automaticamente</option>
                  </select></div>
              </div>
              {form.landing.countdown === "mostra" && form.landing.countdown_scadenza === "rinnova" && (
                <div><label className={labelCls}>Rinnova per</label>
                  <select value={form.landing.rinnova_ore} onChange={(e) => setNested("landing", "rinnova_ore", parseInt(e.target.value))}
                    className={inputCls} data-testid="ced-landing-rinnova">
                    {RINNOVA_OPTS.map(([h, l]) => <option key={h} value={h}>Rinnova automaticamente per {l}</option>)}
                  </select></div>
              )}
              <label className="flex items-center gap-2 text-sm text-zinc-800">
                <input type="checkbox" checked={!!form.landing.mostra_disponibilita}
                  onChange={(e) => setNested("landing", "mostra_disponibilita", e.target.checked)}
                  className="h-4 w-4 accent-zinc-900" data-testid="ced-landing-disponibilita" />
                Mostra sempre la disponibilità ("Solo N coupon rimasti" — richiede Quantità nel tab Impostazioni)
              </label>
              <p className="text-[11px] text-zinc-400">Il conto alla rovescia punta a "Disponibile fino al" (o alla fine della validità). Con "Rinnova" riparte in automatico a ogni scadenza.</p>
            </>
          )}

          {tab === "thankyou" && (
            <>
              <div><label className={labelCls}>Titolo</label>
                <input value={form.thankyou.titolo} onChange={(e) => setNested("thankyou", "titolo", e.target.value)} className={inputCls}
                  placeholder="Coupon scaricato con successo!" data-testid="ced-ty-titolo" /></div>
              <div><label className={labelCls}>Descrizione</label>
                <RichTextEditor value={form.thankyou.descrizione} onChange={(v) => setNested("thankyou", "descrizione", v)} testid="ced-ty-descrizione"
                  placeholder="Mostra questo codice in cassa oppure prenota subito..." /></div>
              <div><label className={labelCls}>Modalità thank you page</label>
                <select value={form.thankyou.modalita} onChange={(e) => setNested("thankyou", "modalita", e.target.value)}
                  className={inputCls} data-testid="ced-ty-modalita">
                  <option value="pagina">Mostra la thank you page (codice + PDF + condivisione)</option>
                  <option value="redirect">Redirect automatico a un link</option>
                </select></div>
              {form.thankyou.modalita === "redirect" && (
                <div><label className={labelCls}>Link redirect</label>
                  <input value={form.thankyou.redirect_url} onChange={(e) => setNested("thankyou", "redirect_url", e.target.value)}
                    placeholder="https://... (vuoto = modulo di prenotazione collegato)" className={inputCls} data-testid="ced-ty-redirect" />
                  <p className="text-[11px] text-zinc-400 mt-1">Dopo il download il cliente viene portato direttamente a questo link — tipicamente il modulo web di prenotazione. Vuoto = il modulo collegato nel tab Generali.</p></div>
              )}
              <div><label className={labelCls}>ID Pixel Facebook</label>
                <input value={form.tracciamenti.fb_pixel_id || ""} onChange={(e) => setNested("tracciamenti", "fb_pixel_id", e.target.value.replace(/\D/g, ""))}
                  placeholder="es. 1234567890123456" className={inputCls} data-testid="ced-trac-pixel" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Evento Google Analytics 4</label>
                  <input value={form.tracciamenti.ga4_event} onChange={(e) => setNested("tracciamenti", "ga4_event", e.target.value)}
                    placeholder="es. coupon_scaricato" className={inputCls} data-testid="ced-trac-ga4" /></div>
                <div><label className={labelCls}>Evento Pixel Facebook (extra)</label>
                  <input value={form.tracciamenti.fb_event} onChange={(e) => setNested("tracciamenti", "fb_event", e.target.value)}
                    placeholder="es. Lead" className={inputCls} data-testid="ced-trac-fb" /></div>
              </div>
              <p className="text-[11px] text-zinc-400">Con l'ID Pixel la landing carica in automatico lo script Meta: PageView alla visita e evento standard <strong>Lead</strong> al download del coupon (più l'evento extra se impostato). Così l'ads Facebook registra le conversioni vere.</p>
            </>
          )}

          {tab === "pdf" && (
            <>
              <label className="flex items-center gap-2 text-sm text-zinc-800">
                <input type="checkbox" checked={!!form.pdf.attivo} onChange={(e) => setNested("pdf", "attivo", e.target.checked)}
                  className="h-4 w-4 accent-zinc-900" data-testid="ced-pdf-attivo" />
                Coupon stampabile in PDF (anche dalla thank-you page del cliente)
              </label>
              <div><label className={labelCls}>Titolo sul PDF</label>
                <input value={form.pdf.titolo} onChange={(e) => setNested("pdf", "titolo", e.target.value)} className={inputCls}
                  placeholder={form.name || "Titolo coupon"} data-testid="ced-pdf-titolo" /></div>
              <div><label className={labelCls}>Testo sul PDF</label>
                <textarea rows={3} value={form.pdf.testo} onChange={(e) => setNested("pdf", "testo", e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="Presenta questo coupon in cassa..." data-testid="ced-pdf-testo" /></div>
              <label className="flex items-center gap-2 text-sm text-zinc-800">
                <input type="checkbox" checked={!!form.pdf.show_qr} onChange={(e) => setNested("pdf", "show_qr", e.target.checked)}
                  className="h-4 w-4 accent-zinc-900" data-testid="ced-pdf-qr" />
                Includi QR code al link pubblico del coupon
              </label>
              <button type="button" onClick={previewPdf} data-testid="ced-pdf-preview"
                className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                Apri anteprima PDF
              </button>
            </>
          )}

          {tab === "email" && (
            <>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800" data-testid="ced-email-info">
                Personalizza la mail di conferma che riceve il cliente quando scarica il coupon. Il <b>QR code</b> e il <b>codice</b> vengono inseriti automaticamente nel corpo della mail. Variabili: <code>{"{nome}"}</code>, <code>{"{coupon}"}</code>, <code>{"{codice}"}</code>, <code>{"{validita}"}</code>. Vuoto = testo standard.
              </div>
              <div><label className={labelCls}>Oggetto della mail</label>
                <input value={form.email.subject} onChange={(e) => setNested("email", "subject", e.target.value)} className={inputCls}
                  placeholder={`Il tuo coupon ${form.name || "…"}`} data-testid="ced-email-subject" /></div>
              <div><label className={labelCls}>Titolo (grande, in alto)</label>
                <input value={form.email.titolo} onChange={(e) => setNested("email", "titolo", e.target.value)} className={inputCls}
                  placeholder="Il tuo coupon è pronto!" data-testid="ced-email-titolo" /></div>
              <div><label className={labelCls}>Testo introduttivo</label>
                <textarea rows={3} value={form.email.testo} onChange={(e) => setNested("email", "testo", e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="Grazie {nome}! Ecco il tuo coupon {coupon}:" data-testid="ced-email-testo" /></div>
              <div><label className={labelCls}>Testo del pulsante prenotazione</label>
                <input value={form.email.cta_label} onChange={(e) => setNested("email", "cta_label", e.target.value)} className={inputCls}
                  placeholder="Prenota ora il tuo tavolo" data-testid="ced-email-cta" /></div>
            </>
          )}

          {tab === "condividi" && (
            <>
              <label className="flex items-center gap-2 text-sm text-zinc-800">
                <input type="checkbox" checked={!!form.referral.attivo} onChange={(e) => setNested("referral", "attivo", e.target.checked)}
                  className="h-4 w-4 accent-zinc-900" data-testid="ced-ref-attivo" />
                Attiva "Condividi ad un amico" sulla thank-you page
              </label>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800" data-testid="ced-ref-info">
                Ogni cliente riceve un <b>link personale tracciato</b> (?ref=codice): quando un amico scarica il coupon da quel link,
                la statistica "da condivisione" del coupon aumenta e vedi chi porta nuovi clienti.
              </div>
              <div><label className={labelCls}>Messaggio di invito</label>
                <textarea rows={3} value={form.referral.messaggio} onChange={(e) => setNested("referral", "messaggio", e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  placeholder="Ho trovato questo coupon da Ninfa Restaurant, approfittane anche tu!"
                  data-testid="ced-ref-messaggio" /></div>
            </>
          )}
        </div>

        <div className="px-5 pb-5">
          <button onClick={save} disabled={saving} data-testid="ced-save"
            className="w-full h-12 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2">
            <FloppyDisk size={16} weight="bold" /> {saving ? "Salvataggio..." : "Salva coupon"}
          </button>
        </div>
      </div>
    </div>
  );
}
